import { AnalyticsEvent } from '~/modules/services/analyticsService';
import { ListingDetailCtaContext } from '../listingCtaAnalytics';
import { ALLOWED_LINKS_AS_TABS, AnalyticsEventTopic, MORE_RESOURCES_EVENT_CONTEXT } from '../analyticsHelper';
import { ElementTitle, ElementType, LISTING_DETAIL_TAB_TITLES } from './constants';
import { GA4ListingDetailClickEvent, GtmEventType } from './gtmEventType';
import { getAppType, getListingAppCost, getQueryStringValue, isAiAssetListing } from './gtmEventTypeHelper';

// Non-navigational link schemes whose href must never be sent as click_url.
// `mailto:` carries the publisher's raw support email (PII); `javascript:`
// (e.g. the Support-tab "Terms & Conditions" link that opens a modal) carries
// no real destination. The click is still recorded — click_text comes from
// the link name, not its URL — only click_url is suppressed.
const NON_NAVIGATIONAL_URL_SCHEME = /^\s*(mailto|javascript):/i;

const RESOURCE_ANALYTICS_CATEGORIES: Record<string, string> = {
    'Data Sheet': 'data sheet',
    Webinar: 'webinars',
    'Customer Testimonial': 'testimonials',
    'Case Study': 'case studies',
    Whitepaper: 'whitepapers',
};

export type ListingDetailsClickAnalyticsEvent = {
    clickUrl: string;
    pricing: string;
    listingRecordType: string;
    sponsoredCampaign: string;
    oafId: string;
    title: string;
    button?: any;
    link?: any;
    publisher: string;
    context: string;
    lead_type?: string;
    caption?: string;
    installMethod?: string;
    filePath?: string;
    customTitle?: string;
    external_id?: string;
    listingDetailCta?: ListingDetailCtaContext;
} & AnalyticsEvent;

export class GtmListingDetailClickType extends GtmEventType {
    static ctaClickElements = [
        'test drive',
        'get it now',
        'watch demo',
        'add to favorites',
        'remove from favorites',
        'try it free',
        'request private offer',
        'submit offer request',
        'learn more',
    ];

    validClickElements = [
        ...GtmListingDetailClickType.ctaClickElements,
        'pricing details',
        // Trust center — V1 renders it in the Overview and More Details panels;
        // Listing Detail V2 consolidates it into the Security tab.
        'overview trust center',
        'more details trust center',
        'security trust center',
        'competencies_certifications_modal_open_category',
        'competencies_certifications_modal_open_subcategory',
        'competencies_metrics_modal_open',
        'competencies_industry_row_expand',
        'competencies_industry_row_collapse',
        'competencies_product_row_expand',
        'competencies_product_row_collapse',
        // Install-card buttons (skill/plugin sidebars) — same allowlist path as elementType === 'tab' below.
        'copy-install-command',
        'view-source',
        'copy-install-prompt',
        'get-install-prompt',
        // Package Files viewer (Files tab): open a file, copy its contents, collapse/expand the tree.
        'view-file',
        'copy-file',
        'hide-file-tree',
        'show-file-tree',
        // Support tab (Listing Detail V2) external links — the Contact "Website" / "Email"
        // and Help "Knowledge Center" anchors. The link name doubles as the GA4 click_text.
        'support website',
        'support email',
        'knowledge center',
        // Support tab (Listing Detail V2) Legal — the "Terms & Conditions" link, which opens
        // the T&C modal rather than navigating, and the Salesforce "Terms of Service" link
        // inside that modal. Same wds-link path; name doubles as click_text.
        'terms and conditions',
        'terms of service',
    ];

    // @ts-expect-error * Automated Type Error Fix
    canHandle(event): boolean {
        return event.topic === AnalyticsEventTopic.ANALYTICS_CLICK && event.oafId && this.isValidElementClick(event);
    }

    // @ts-expect-error * Automated Type Error Fix
    isValidElementClick(event) {
        const buttonName = event.button?.callToAction?.name || event.link?.callToAction?.name;
        return (
            !!event.listingDetailCta ||
            this.validClickElements.includes(buttonName) ||
            event.elementType === 'tab' ||
            (event.elementType === 'link' && ALLOWED_LINKS_AS_TABS.includes(event.link?.callToAction?.name)) ||
            (event.elementType === 'link' && MORE_RESOURCES_EVENT_CONTEXT === event.context)
        );
    }

    getBody(event: ListingDetailsClickAnalyticsEvent): GA4ListingDetailClickEvent {
        const {
            clickUrl,
            pricing,
            listingRecordType,
            sponsoredCampaign,
            recommended,
            oafId,
            title,
            url,
            button,
            link,
            installMethod,
            filePath,
        } = event;
        const rawClickUrl = clickUrl || link?.callToAction?.url || url;
        // Suppress non-navigational schemes (mailto:/javascript:) so PII and
        // modal-opening stubs never land in click_url. Leaving it undefined
        // omits the field from GA4 rather than substituting the page URL.
        const elementClickUrl = NON_NAVIGATIONAL_URL_SCHEME.test(rawClickUrl ?? '') ? undefined : rawClickUrl;
        const resourceSupportTitle = this.getResourceSupportTitle(event);
        const body = {
            event: 'custEv_appEngagement',
            click_text:
                event.listingDetailCta?.clickText ||
                button?.callToAction?.name ||
                button?.callToAction?.label ||
                link?.callToAction?.name ||
                link?.callToAction?.label ||
                event.caption,
            click_url: elementClickUrl,
            element_title:
                event.listingDetailCta?.elementTitle ??
                resourceSupportTitle ??
                GtmListingDetailClickType.getItemTitle(event),
            element_type:
                event.listingDetailCta?.elementType ??
                (resourceSupportTitle ? ElementType.LINK_TEXT : GtmListingDetailClickType.getElementType(event)),
            internal_driver: getQueryStringValue(elementClickUrl, 'd'),
            lp_detail_interaction: true,
            app_cost: getListingAppCost(pricing, oafId, listingRecordType),
            app_type: getAppType(listingRecordType),
            featured: !!sponsoredCampaign,
            recommended: recommended,
            ignore_hover: false,
            internal_article: false,
            internal_source: getQueryStringValue(elementClickUrl, 'channel'),
            listing_id: oafId,
            listing_name: title,
            listing_type: listingRecordType,
            listing_url: url,
            hover_intent: false,
            page_type: 'listing details',
            install_method: installMethod,
            file_path: filePath,
        };

        return body;
    }

    private getResourceSupportTitle(event: ListingDetailsClickAnalyticsEvent): string | undefined {
        if (
            event.elementType !== 'link' ||
            isAiAssetListing(event.external_id || event.oafId, event.listingRecordType)
        ) {
            return undefined;
        }
        if (event.context === MORE_RESOURCES_EVENT_CONTEXT && event.customTitle) {
            const resourceType = event.customTitle.trim();
            return `resources:${RESOURCE_ANALYTICS_CATEGORIES[resourceType] || resourceType.toLowerCase()}`;
        }
        switch (event.link?.callToAction?.name) {
            case 'terms and conditions':
                return ElementTitle.SUPPORT_LEGAL;
            case 'support website':
            case 'support email':
                return ElementTitle.SUPPORT_CONTACT;
            default:
                return undefined;
        }
    }

    // @ts-expect-error * Automated Type Error Fix
    public static getLeadEventType(event): string {
        const eventLeadText = event.button?.callToAction?.name || event.link?.callToAction?.name;
        if (eventLeadText === 'test drive' || eventLeadText === 'watch demo') {
            return eventLeadText;
        }
        // @ts-expect-error * Automated Type Error Fix
        return undefined;
    }

    // @ts-expect-error * Automated Type Error Fix
    public static getItemTitle(event) {
        if (event.elementType === ElementType.TAB) {
            const tabId = event.button?.callToAction?.name || event.link?.callToAction?.name;
            const tabTitle = LISTING_DETAIL_TAB_TITLES[tabId] ?? tabId?.match(/^(.+)-tab$/)?.[1];
            if (tabTitle) {
                return tabTitle;
            }
        }
        return event.lead_type === 'demo' ? ElementTitle.WATCH_DEMO : event.customTitle || event.title;
    }

    // @ts-expect-error * Automated Type Error Fix
    public static isCtaClick(event): boolean {
        const buttonName = event.button?.callToAction?.name || event.link?.callToAction?.name;
        return GtmListingDetailClickType.ctaClickElements.includes(buttonName);
    }

    // @ts-expect-error * Automated Type Error Fix
    public static getElementType(event): string {
        const buttonName = event.button?.callToAction?.name || event.link?.callToAction?.name;
        if (event.elementType === ElementType.BANNER && buttonName === 'watch demo') {
            return ElementType.BANNER;
        }
        return GtmListingDetailClickType.ctaClickElements.includes(buttonName) ? 'button' : event.elementType;
    }
}
